import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldError } from '@/components/ui/field-error';

interface Props {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RucFormDialog({ tenantId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    ruc: '',
    razonSocial: '',
    regimen: 'iva_irp' as 'iva_irp' | 'ire_simple',
    isPrimary: false,
  });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api(`/api/admin/tenants/${tenantId}/rucs`, {
        method: 'POST',
        body: JSON.stringify({
          ruc: form.ruc.trim(),
          razonSocial: form.razonSocial.trim(),
          regimen: form.regimen,
          obligaciones: [],
          isPrimary: form.isPrimary,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      onOpenChange(false);
      setForm({ ruc: '', razonSocial: '', regimen: 'iva_irp', isPrimary: false });
      setIssues({});
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.body.error === 'ruc_already_exists') {
          setIssues({ ruc: ['Ya existe este RUC para el contacto'] });
        } else if (err.body.issues) {
          setIssues(err.body.issues);
        } else {
          setIssues({ _form: [err.body.error] });
        }
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar RUC</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ruc" required>
              RUC
            </Label>
            <Input
              id="ruc"
              placeholder="4567890-1"
              value={form.ruc}
              onChange={(e) => setForm({ ...form, ruc: e.target.value })}
              required
            />
            <FieldError error={issues.ruc} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="razonSocial" required>
              Razón social (en SET)
            </Label>
            <Input
              id="razonSocial"
              placeholder="GONZALEZ MARIA"
              value={form.razonSocial}
              onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
              required
            />
            <FieldError error={issues.razonSocial} />
          </div>
          <div className="space-y-1.5">
            <Label required>Régimen</Label>
            <Select
              value={form.regimen}
              onValueChange={(v) => setForm({ ...form, regimen: v as 'iva_irp' | 'ire_simple' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="iva_irp">IVA + IRP</SelectItem>
                <SelectItem value="ire_simple">IRE Simple</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Checkbox
              checked={form.isPrimary}
              onCheckedChange={(v) => setForm({ ...form, isPrimary: v === true })}
            />
            Marcar como RUC principal
          </label>

          {issues._form && <p className="text-sm text-destructive">Error: {issues._form[0]}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Agregando…' : 'Agregar RUC'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
